import React, { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { siRatingApis } from "../../../api/siRatingApis";
import SiRatingRunForm from "./SiRatingRunForm";
import SiRatingResults from "./SiRatingResults";
import "./siRating.css";

const POLL_MS = 5000;

export default function SiRatingPanel({ initialRatingId, onRatingSelected }) {
  const [ratings, setRatings] = useState([]);
  const [activeId, setActiveId] = useState(initialRatingId || "");
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refreshList = useCallback(() => {
    siRatingApis
      .listMine()
      .then(setRatings)
      .catch(() => setRatings([]));
  }, []);

  useEffect(refreshList, [refreshList]);

  useEffect(() => {
    if (!activeId) {
      setRating(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError("");
    siRatingApis
      .get(activeId)
      .then((r) => active && setRating(r))
      .catch((err) => active && setError(err?.response?.data?.message || err?.message || "Failed to load SI Rating"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [activeId]);

  const processing = (rating?.pdfReports || []).some((p) => p.status === "processing");
  useEffect(() => {
    if (!processing || !rating?._id) return undefined;
    const t = setInterval(() => {
      siRatingApis.get(rating._id).then(setRating).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(t);
  }, [processing, rating?._id]);

  const select = (id) => {
    setActiveId(id);
    onRatingSelected?.(id);
  };

  const handleChange = (updated) => {
    setRating(updated);
    refreshList();
  };

  return (
    <div className="si-rating-layout">
      <aside className="si-rating-aside">
        <SiRatingRunForm
          onCreated={(r) => {
            setRating(r);
            setActiveId(r._id);
            onRatingSelected?.(r._id);
            refreshList();
          }}
        />
        {ratings.length > 0 && (
          <div className="si-rating-card">
            <h3>Previous SI Ratings</h3>
            <ul className="si-rating-list">
              {ratings.map((r) => (
                <li key={r._id}>
                  <button
                    type="button"
                    className={String(r._id) === String(activeId) ? "active" : ""}
                    onClick={() => select(r._id)}
                  >
                    <span>
                      <b>{r.companyName || r.sdm?.projectName || "Company"}</b>
                      <span className="si-rating-muted">
                        {r.ratingSector} · {r.year || "—"} · {r.status === "finalized" ? "Final" : "Draft"}
                      </span>
                    </span>
                    <span className="si-rating-list__grade">
                      {r.grade || "—"}
                      <small>{r.score != null ? Number(r.score).toFixed(1) : ""}</small>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>
      <main className="si-rating-main">
        {loading && !rating ? (
          <div className="si-rating-card si-rating-card--center">
            <CircularProgress size={28} />
          </div>
        ) : error ? (
          <div className="si-rating-card">
            <p className="si-rating-error">{error}</p>
          </div>
        ) : rating ? (
          <SiRatingResults rating={rating} onChange={handleChange} />
        ) : (
          <div className="si-rating-card si-rating-empty">
            <h3>No SI Rating selected</h3>
            <p className="si-rating-muted">
              Pick an SDM project and run the rating, or open a previous rating from the list.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
